import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import SliderModule from 'react-slick';

import { Button, NextArrow, PrevArrow } from '@/ui/index';
import { useWindowSize } from '@/hooks/index';
import useSlickPositioning from '@/hooks/useSlickPositioning';
import { breakPoints } from '@/constants/base';

import styles from './BrandSlider.module.scss';

const BrandSlider = ({ data, business }) => {
  const [windowsX] = useWindowSize();
  const elementRef = useRef(null);
  const isMobile = windowsX < breakPoints.sm;
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useSlickPositioning();

  useEffect(() => {
    if (elementRef.current) {
      const elementWidth = elementRef.current.clientWidth;
      document
        .querySelector('.selector-slick-brand')
        .style.setProperty('--gap', `${elementWidth}px`);
    }
  }, [elementRef]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    focusOnSelect: false,
    pauseOnHover: false,
    draggable: true,
    autoplaySpeed: 5000,
    lazyLoad: 'ondemand',
    renderMode: 'performance',
    appendDots: (dots) => <ul ref={elementRef}>{dots}</ul>,
    customPaging: () => (
      <div className='ft-slick__dots--custom'>
        <div className='loading' />
      </div>
    ),
    nextArrow: <NextArrow brand />,
    prevArrow: <PrevArrow brand />,
  };

  const renderSlider = (item, index) => {
    return (
      <div
        className={styles.branSlider_wrapper}
        key={index}
        itemScope
        itemType='https://schema.org/Offer'
      >
        <div
          className={classNames([
            styles.content,
            { [styles.midPosition]: !item.heading_text },
          ])}
        >
          {item?.heading_text && (
            <span className={styles.label}>{item.heading_text}</span>
          )}
          {item?.title && index === 1 ? (
            <h1 className={styles.title}>{item?.title}</h1>
          ) : (
            <h2 className={styles.title}>{item?.title}</h2>
          )}
          {(item?.button_slug || item?.external_link) && item?.button_text && (
            <Button
              size='shortTrapezoid'
              variant='trapezoidRed'
              href={item?.button_slug || item?.external_link}
              Component={item?.button_slug ? 'router' : 'a'}
              target={item?.external_link && '_blank'}
            >
              {item?.button_text}
            </Button>
          )}
        </div>
        <div
          className={classNames([
            styles.slide_img,
            { [styles.slide_img__hidden]: !isImageLoaded },
          ])}
        >
          {item?.mobile_image && item?.image && (
            <div className={styles.wrapperDiv}>
              {!isImageLoaded && (
                <Skeleton
                  width='100%'
                  height='100%'
                  className={styles.skeleton}
                  containerClassName={styles.skeletonContainer}
                />
              )}
              <Image
                src={
                  isMobile && item?.mobile_image
                    ? item?.mobile_image
                    : item?.image
                }
                width={1440}
                height={642}
                alt={item?.title || 'Bakcell slide image'}
                title={item?.title || 'Bakcell slide image'}
                layout='responsive'
                loading='eager'
                objectFit={isMobile ? 'unset' : 'cover'}
                onLoadingComplete={() => setIsImageLoaded(true)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <SliderModule
      className={classNames([
        'selector-slick-brand',
        'slick-brand-slider',
        styles.slick_brand,
      ])}
      {...settings}
    >
      {data?.items?.map(renderSlider)}
    </SliderModule>
  );
};

export default BrandSlider;
